import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Mail, CheckCircle, XCircle, RefreshCw, Send, Info, Clock } from 'lucide-react';

interface EmailQueueItem {
  id: string;
  refund_request_id: string;
  recipient_email: string;
  email_type: string;
  subject: string;
  body_preview: string;
  status: 'pending' | 'sent' | 'failed';
  error_message: string | null;
  attempts: number;
  last_attempted_at: string | null;
  created_at: string;
  sent_at: string | null;
}

interface EmailQueueStats {
  total: number;
  pending: number;
  sent: number;
  failed: number;
  retry_eligible: number;
}

interface Props {
  supabase: ReturnType<typeof createClient>;
  isDevelopment?: boolean;
  userRole?: 'admin' | 'super_admin' | null;
}

export const EmailQueueManagement: React.FC<Props> = ({ supabase, isDevelopment = true, userRole }) => {
  const [emails, setEmails] = useState<EmailQueueItem[]>([]);
  const [stats, setStats] = useState<EmailQueueStats>({
    total: 0,
    pending: 0,
    sent: 0,
    failed: 0,
    retry_eligible: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'pending' | 'sent' | 'failed'>('all');
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  const tablePrefix = isDevelopment ? 'D_' : 'P_';

  const fetchEmailQueue = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch email queue data
      let query = supabase
        .from(`${tablePrefix}view_email_queue`)
        .select('*')
        .order('created_at', { ascending: false });

      if (selectedStatus !== 'all') {
        query = query.eq('status', selectedStatus);
      }

      const { data, error } = await query.limit(100);

      if (error) throw error;

      // Properly type the data from Supabase
      const typedData: EmailQueueItem[] = (data || []).map((item: any) => ({
        id: item.id,
        refund_request_id: item.refund_request_id,
        recipient_email: item.recipient_email,
        email_type: item.email_type,
        subject: item.subject,
        body_preview: item.body_preview,
        status: item.status,
        attempts: item.attempts || 0,
        last_attempted_at: item.last_attempted_at,
        error_message: item.error_message,
        created_at: item.created_at,
        sent_at: item.sent_at
      }));

      setEmails(typedData);

      // Calculate statistics
      const statsData = {
        total: typedData.length,
        pending: typedData.filter(e => e.status === 'pending').length,
        sent: typedData.filter(e => e.status === 'sent').length,
        failed: typedData.filter(e => e.status === 'failed').length,
        retry_eligible: typedData.filter(e => e.status === 'failed' && e.attempts < 3).length
      };
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching email queue:', error);
    } finally {
      setLoading(false);
    }
  }, [supabase, tablePrefix, selectedStatus]);

  useEffect(() => {
    fetchEmailQueue();
  }, [selectedStatus, fetchEmailQueue]);

  const retryEmail = async (emailId: string) => {
    setRetryingId(emailId);
    try {
      // Find the email to get refund_request_id
      const email = emails.find(e => e.id === emailId);
      if (!email) return;
      
      // Call the manual notification function
      const { error } = await supabase.rpc('send_refund_notification_direct', {
        p_refund_request_id: emailId, // This would need the actual refund_request_id
        p_notification_type: 'status_update'
      });

      if (error) throw error;

      alert('Email retry initiated successfully');
      fetchEmailQueue(); // Refresh the list
    } catch (error) {
      console.error('Error retrying email:', error);
      alert('Failed to retry email. Please check logs.');
    } finally {
      setRetryingId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <Mail className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Email Queue Management</h2>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
          >
            <Info className="w-4 h-4 mr-2" />
            Help & Troubleshooting
          </button>
        </div>
        <p className="mt-2 text-gray-600">
          Monitor and manage failed email notifications from refund requests
        </p>
      </div>

      {/* Help Section */}
      {showHelp && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">📧 Email Queue Troubleshooting Guide</h3>
          
          <div className="space-y-3 text-sm text-blue-800">
            <div>
              <strong>Common Issues & Solutions:</strong>
            </div>
            
            <div className="pl-4 space-y-2">
              <div>
                <span className="font-semibold">1. Failed Emails (Service Key Error):</span>
                <ul className="list-disc pl-5 mt-1">
                  <li>Check if RESEND_API_KEY is configured in Supabase secrets</li>
                  <li>Verify service role key is set in vault</li>
                  <li>Command: <code className="bg-white px-1 py-0.5 rounded">supabase secrets list</code></li>
                </ul>
              </div>

              <div>
                <span className="font-semibold">2. Emails Stuck in Pending:</span>
                <ul className="list-disc pl-5 mt-1">
                  <li>Check if refund-notify Edge Function is deployed</li>
                  <li>Verify Edge Function logs for errors</li>
                  <li>Command: <code className="bg-white px-1 py-0.5 rounded">supabase functions logs refund-notify</code></li>
                </ul>
              </div>

              <div>
                <span className="font-semibold">3. Max Retries Reached:</span>
                <ul className="list-disc pl-5 mt-1">
                  <li>Manual intervention required</li>
                  <li>Check error message for specific issue</li>
                  <li>May need to update email service configuration</li>
                </ul>
              </div>

              <div>
                <span className="font-semibold">4. No Emails Being Sent:</span>
                <ul className="list-disc pl-5 mt-1">
                  <li>Verify HTTP/NET extension is enabled in database</li>
                  <li>Check trigger exists: <code className="bg-white px-1 py-0.5 rounded">SELECT * FROM pg_trigger WHERE tgname LIKE '%refund%'</code></li>
                  <li>Ensure notification trigger is active on refund_requests table</li>
                </ul>
              </div>
            </div>

            <div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-200">
              <strong className="text-yellow-900">⚠️ Important:</strong>
              <ul className="list-disc pl-5 mt-1 text-yellow-800">
                <li>Failed emails are automatically queued for retry (up to 3 attempts)</li>
                <li>Successful emails are kept for 30 days for audit purposes</li>
                <li>Always check Edge Function logs when troubleshooting</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Emails</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Mail className="w-8 h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Sent</p>
              <p className="text-2xl font-bold text-green-600">{stats.sent}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Retry Eligible</p>
              <p className="text-2xl font-bold text-blue-600">{stats.retry_eligible}</p>
            </div>
            <RefreshCw className="w-8 h-8 text-blue-400" />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-1 mb-6">
        {(['all', 'pending', 'sent', 'failed'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(status)}
            className={`px-4 py-2 font-medium rounded-lg transition-colors ${
              selectedStatus === status
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status === 'all' && ` (${stats.total})`}
            {status === 'pending' && ` (${stats.pending})`}
            {status === 'sent' && ` (${stats.sent})`}
            {status === 'failed' && ` (${stats.failed})`}
          </button>
        ))}
        <button
          onClick={fetchEmailQueue}
          className="ml-auto px-4 py-2 font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Email Queue Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preview
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Attempts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    Loading email queue...
                  </td>
                </tr>
              ) : emails.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    No emails found in the queue
                  </td>
                </tr>
              ) : (
                emails.map((email) => (
                  <tr key={email.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(email.status)}
                        <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(email.status)}`}>
                          {email.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {email.recipient_email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {email.subject}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="max-w-xs truncate" title={email.body_preview}>
                        {email.body_preview}
                      </div>
                      {email.error_message && (
                        <div className="mt-1 text-xs text-red-600">
                          Error: {email.error_message}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {email.attempts}/3
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(email.created_at).toLocaleDateString()}
                      <br />
                      <span className="text-xs">
                        {new Date(email.created_at).toLocaleTimeString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {email.status === 'failed' && email.attempts < 3 && (
                        <button
                          onClick={() => {
                            if (userRole !== 'super_admin') {
                              alert('super_admin permission required.');
                              return;
                            }
                            retryEmail(email.id);
                          }}
                          disabled={retryingId === email.id || userRole !== 'super_admin'}
                          className="text-blue-600 hover:text-blue-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                          title={userRole !== 'super_admin' ? 'super_admin permission required' : ''}
                        >
                          {retryingId === email.id ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                              Retrying...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              Retry
                            </>
                          )}
                        </button>
                      )}
                      {email.status === 'failed' && email.attempts >= 3 && (
                        <span className="text-red-600 text-xs">Max retries reached</span>
                      )}
                      {email.status === 'sent' && (
                        <span className="text-green-600 text-xs">Successfully sent</span>
                      )}
                      {email.status === 'pending' && (
                        <span className="text-yellow-600 text-xs">Queued for sending</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SQL Query Helper */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-2">Useful SQL Queries:</h4>
        <div className="space-y-2 text-sm">
          <div className="p-2 bg-white rounded border border-gray-200">
            <code className="text-xs">
              -- Check failed emails<br />
              SELECT * FROM "{tablePrefix}view_email_queue" WHERE status = 'failed';
            </code>
          </div>
          <div className="p-2 bg-white rounded border border-gray-200">
            <code className="text-xs">
              -- Test email notification manually<br />
              SELECT send_refund_notification_direct('refund-request-id'::UUID, 'status_update');
            </code>
          </div>
          <div className="p-2 bg-white rounded border border-gray-200">
            <code className="text-xs">
              -- Check if triggers are active<br />
              SELECT tgname FROM pg_trigger WHERE tgname LIKE '%refund%notify%';
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};